import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useScope } from '../hooks/useScope';
import useUrlParams from '../hooks/useUrlParams';
import { appearancesLabel } from '../utils/format';
import { LobbyistDoc, MemberIndexEntry, RegistryLobbyist } from '../types/types';
import { getLobbyistKnessets, getLobbyistRegistry, getMemberSummaries } from '../data/cache';
import TabBar from '../components/TabBar';
import { photoFor } from '../utils/names';
import { color, font, radius, space, text } from '../theme';
import DetailHeader from '../components/DetailHeader';
import { Card, CardHeader, Section } from '../components/Card';
import { StatTile, StatTileRow } from '../components/StatTile';
import { Badge } from '../components/Badge';
import Bar from '../components/Bar';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import Notice from '../components/Notice';
import { EmptyState, ErrorState } from '../components/States';
import { Skeleton, SkeletonList, SkeletonText } from '../components/Skeleton';
import ClientsCard from '../components/lobbyists/ClientsCard';
import { RegisterLine, RegistryBadges, RegistrySource } from '../components/lobbyists/RegistryBits';
import { registryIdFromParam } from '../components/lobbyists/registry';
import { useBreakpoint } from '../useBreakpoint';
import PageContainer from '../layout/PageContainer';
import DetailHeaderFrame from '../layout/DetailHeaderFrame';
import TwoPane from '../layout/TwoPane';

const monthYear = (iso: string, month: 'long' | 'short') =>
  new Date(iso).toLocaleDateString('he-IL', { year: 'numeric', month });

const activeRange = (l: LobbyistDoc) => {
  if (!l.firstSeen || !l.lastSeen) return '';
  if (l.firstSeen === l.lastSeen) return `הופעה ב${monthYear(l.firstSeen, 'long')}`;
  return `פעיל/ה ${monthYear(l.firstSeen, 'short')} – ${monthYear(l.lastSeen, 'short')}`;
};

const LOBBYISTS_TAB = '/committees?tab=lobbyists';

/* /lobbyists/:lobbyistId — a lobbyist seen at committee sessions (a lobbyists/{id} doc, or an
   archived Knesset's copy), with the register's details when the sync matched them; or
   /lobbyists/reg-{registryId}, a licensed lobbyist known only from the register (no committee
   appearances recorded). Someone seen in more than one Knesset gets a Knesset switch (?k=),
   as on MK pages; the latest Knesset they appear in is the default. */
const LobbyistDetailPage = () => {
  const { lobbyistId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useBreakpoint();
  const [params, setParams] = useUrlParams();
  // Every Knesset the lobbyist appears in, the latest first
  const [present, setPresent] = useState<{ k: number; doc: LobbyistDoc }[]>([]);
  // The register's entry: beside the doc, or alone on a register-only page
  const [entry, setEntry] = useState<RegistryLobbyist | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const scope = useScope();
  const [members, setMembers] = useState<MemberIndexEntry[] | null>(null);
  // The caller's state (the back link's `from`), kept on the redirect from a reg- address
  const navState = useRef<unknown>(location.state);
  navState.current = location.state;

  useEffect(() => {
    if (!lobbyistId) return;
    let cancelled = false;
    setStatus('loading');
    const registryId = registryIdFromParam(lobbyistId);

    if (registryId !== null) {
      // A register-only page. If the sync has since matched the lobbyist to a doc of ours (in
      // any Knesset), the doc's page is the one to show.
      getLobbyistRegistry().then(reg => {
        if (cancelled) return undefined;
        const e = reg && Object.prototype.hasOwnProperty.call(reg.lobbyists, registryId) ? reg.lobbyists[registryId] : null;
        if (!e) { setStatus('notFound'); return undefined; }
        const showRegisterOnly = () => { setPresent([]); setEntry(e); setStatus('ready'); };
        if (!e.docId) { showRegisterOnly(); return undefined; }
        const docId = e.docId;
        return getLobbyistKnessets(docId).then(list => {
          if (cancelled) return;
          if (list.length > 0) navigate(`/lobbyists/${encodeURIComponent(docId)}`, { replace: true, state: navState.current });
          else showRegisterOnly();
        });
      }).catch(() => { if (!cancelled) setStatus('error'); });
      return () => { cancelled = true; };
    }

    // The register is optional here: without it the page is as it was before the register
    Promise.all([getLobbyistKnessets(lobbyistId), getLobbyistRegistry().catch(() => null)]).then(([list, reg]) => {
      if (cancelled) return;
      const e = reg && Object.prototype.hasOwnProperty.call(reg.byDocId, lobbyistId) ? reg.byDocId[lobbyistId] : null;
      if (list.length === 0) {
        // A doc the register still points at but that's gone: what the register knows
        if (e) { setPresent([]); setEntry(e); setStatus('ready'); } else setStatus('notFound');
        return;
      }
      setPresent(list);
      setEntry(e);
      setStatus('ready');
    }).catch(() => { if (!cancelled) setStatus('error'); });
    return () => { cancelled = true; };
  }, [lobbyistId, reloadKey, navigate]);

  // The Knesset shown: ?k= when the lobbyist appears in it, else the latest they appear in
  const kParam = params.get('k');
  const shown = present.find(p => String(p.k) === kParam) || present[0] || null;
  const lobbyist = shown ? shown.doc : null;

  // MKs on the committees where the lobbyist appeared in that Knesset, from the member index
  useEffect(() => {
    if (!lobbyist) return undefined;
    let cancelled = false;
    setMembers(null);
    const ids = lobbyist.memberIds || [];
    getMemberSummaries(ids).then(byId => {
      if (cancelled) return;
      const list = ids.map(id => byId[id]).filter((m): m is MemberIndexEntry => Boolean(m));
      list.sort((a, b) => a.name.localeCompare(b.name, 'he'));
      setMembers(list);
    }).catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [lobbyist]);

  if (status === 'loading') {
    return (
      <Layout>
        <div data-skeleton="true" style={{ padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <Card><div style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><Skeleton width={100} height={20} /><Skeleton width="60%" height={24} /><SkeletonText lines={1} /></div></Card>
          <SkeletonList rows={4} />
        </div>
      </Layout>
    );
  }
  if (status === 'error') {
    return <Layout><div style={{ padding: space.lg }}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div></Layout>;
  }
  if (status === 'notFound' || (!lobbyist && !entry)) {
    return (
      <Layout>
        <div style={{ padding: space.lg }}>
          <Card padding={0}>
            <EmptyState icon="user" title="השדלן/ית לא נמצא/ה" body="ייתכן שהקישור שגוי." action={<Button onClick={() => navigate(LOBBYISTS_TAB, { replace: true })}>לכל השדלנים</Button>} />
          </Card>
        </div>
      </Layout>
    );
  }

  /* ── Register only ("3 · מצב"): the register's header and clients, no activity ── */
  if (!lobbyist && entry) {
    return (
      <Layout width="full" breadcrumbs={[{ label: 'שדלנים', to: LOBBYISTS_TAB }, { label: entry.fullName }]}>
        <PageContainer>
        <TwoPane sideWidth={320} side={<DetailHeaderFrame>
        <DetailHeader fallback={LOBBYISTS_TAB} fallbackLabel="שדלנים">
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
            <span style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}><RegistryBadges entry={entry} /></span>
            <h1 style={text.entityTitle}>{entry.fullName}</h1>
            <RegisterLine entry={entry} />
          </div>
        </DetailHeader>
        </DetailHeaderFrame>}>

        <div style={{ padding: isDesktop ? 0 : space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
          <Notice>לא נמצאו הופעות שלו/ה בישיבות ועדות הכנסת ה-{[scope.knesset].concat(scope.archivedKnessets.filter(k => k !== scope.knesset).sort((a, b) => b - a)).join(' וה-')}. המידע בדף הזה מפנקס השדלנים בלבד.</Notice>
          <StatTileRow columns={2}>
            <StatTile value={entry.clients.length} label="לקוחות" />
            <StatTile value={0} label="הופעות בוועדות" />
          </StatTileRow>
          {entry.clients.length > 0 && <ClientsCard clients={entry.clients} />}
          <RegistrySource />
        </div>
        </TwoPane>
        </PageContainer>
      </Layout>
    );
  }

  const l = lobbyist as LobbyistDoc;
  const shownK = (shown as { k: number }).k;
  const from = l.fullName;
  const primaryOrgType = l.organizationTypes?.[0];

  // Seen in more than one Knesset: a switch between them, the latest first (as on MK pages)
  const knessetSwitch = present.length > 1 ? (
    <TabBar
      label="כנסת"
      tabs={present.map(p => ({ key: String(p.k), label: `הכנסת ה-${p.k}` }))}
      active={String(shownK)}
      onChange={key => setParams(prev => {
        const next = new URLSearchParams(prev);
        if (key === String(present[0].k)) next.delete('k'); else next.set('k', key);
        return next;
      })}
    />
  ) : null;

  return (
    <Layout width="full" breadcrumbs={[{ label: 'שדלנים', to: LOBBYISTS_TAB }, { label: l.fullName }]}>
      <PageContainer>
      {/* Desktop: the header card is a sticky side panel beside the activity */}
      <TwoPane sideWidth={320} mainHeader={isDesktop && knessetSwitch ? <div style={{ marginBottom: space.lg }}>{knessetSwitch}</div> : undefined} side={<DetailHeaderFrame>
      <DetailHeader fallback={LOBBYISTS_TAB} fallbackLabel="שדלנים">
        <div style={{ display: 'flex', flexDirection: 'column', gap: space.sm }}>
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}>
            {entry && <RegistryBadges entry={entry} />}
            {l.isLoby99 && <Badge tone="info">לובי 99</Badge>}
            {(l.titles || []).map((t, i) => <Badge key={i}>{t}</Badge>)}
            {!l.isLoby99 && primaryOrgType && <Badge>{primaryOrgType}</Badge>}
          </span>
          <h1 style={text.entityTitle}>{l.fullName}</h1>
          {entry && <RegisterLine entry={entry} />}
          {activeRange(l) && <div style={{ fontSize: font.small, color: color.textSecondary }}>{activeRange(l)} · בישיבות ועדות הכנסת ה-{shownK}</div>}
        </div>
      </DetailHeader>
      </DetailHeaderFrame>}>

      <div style={{ padding: isDesktop ? 0 : space.lg, display: 'flex', flexDirection: 'column', gap: space.lg }}>
        {!isDesktop && knessetSwitch}
        {/* Seen only in an earlier Knesset: say so, rather than leave the current one unmentioned */}
        {present[0].k !== scope.knesset && (
          <Notice>בוועדות הכנסת ה-{scope.knesset} עוד לא נרשמו לו/ה הופעות. מוצגת הפעילות בכנסת ה-{shownK}.</Notice>
        )}
        <StatTileRow columns={3}>
          <StatTile value={l.totalAppearances} label="הופעות" />
          <StatTile value={l.committees.length} label="ועדות" />
          <StatTile value={members ? members.length : '…'} label="ח״כים בוועדות" />
        </StatTileRow>

        {/* The register: the clients the lobbyist declared, then where that comes from */}
        {entry && entry.clients.length > 0 && <ClientsCard clients={entry.clients} />}
        {entry && <RegistrySource />}

        {l.isLoby99 && l.internalRoles?.length > 0 && (
          <Card>
            <CardHeader title="תפקיד בלובי 99" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}>
              {l.internalRoles.map((role, i) => <Badge key={i} shape="pill" tone="info">{role}</Badge>)}
            </div>
          </Card>
        )}
        {!l.isLoby99 && l.organizations?.length > 0 && (
          <Card>
            <CardHeader title="ארגונים מיוצגים" meta={l.organizations.length} />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}>
              {l.organizations.map((org, i) => <Badge key={i} shape="pill">{org}</Badge>)}
            </div>
          </Card>
        )}

        {l.committees.length > 0 && (
          <Section title="פעילות לפי ועדה" meta={l.committees.length}>
            <Card padding={0}>
              {l.committees.map((c, i) => (
                <ListRow
                  key={c.committeeId || i}
                  divider={i > 0}
                  to={`/committees/${c.committeeId}`}
                  state={{ from }}
                  title={c.committeeName}
                  subtitle={
                    <span style={{ display: 'flex', flexDirection: 'column', gap: space.sm, marginTop: space.xs }}>
                      <Bar value={c.appearances} max={l.totalAppearances || 1} height={5} />
                      {c.organizations && c.organizations.length > 0 && (
                        <span style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs }}>
                          {c.organizations.map((org, j) => <Badge key={j}>{org}</Badge>)}
                        </span>
                      )}
                    </span>
                  }
                  trailing={<Badge tone="info" style={{ flexShrink: 0 }}>{appearancesLabel(c.appearances)}</Badge>}
                />
              ))}
            </Card>
          </Section>
        )}

        {members === null && <SkeletonList rows={3} avatar />}
        {members && members.length > 0 && (
          <Card>
            <CardHeader title="חברי כנסת בוועדות אלה" meta={members.length} />
            <p style={{ margin: `0 0 ${space.md}px`, fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>
              חברי הוועדות שבישיבותיהן השתתף/ה. אין בכך כדי להעיד על פגישה ביניהם.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.sm }}>
              {members.map(mk => (
                <Link
                  key={mk.id}
                  to={`/members/${mk.id}`}
                  state={{ from }}
                  style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, minHeight: 36, padding: `2px ${space.md - 2}px 2px ${space.xs}px`, borderRadius: radius.pill, background: color.surfaceMuted, color: color.text, textDecoration: 'none', fontSize: font.small }}
                >
                  <Avatar name={mk.name} src={photoFor(mk, 28)} size={28} />
                  {mk.name}
                </Link>
              ))}
            </div>
          </Card>
        )}
      </div>
      </TwoPane>
      </PageContainer>
    </Layout>
  );
};

export default LobbyistDetailPage;
